import {
    useSignal,
    useSignalEffect,
    useSignals,
} from '@preact/signals-react/runtime'
import {Button} from 'primereact/button'
import {Checkbox} from 'primereact/checkbox'
import {Dropdown} from 'primereact/dropdown'
import {MultiSelect} from 'primereact/multiselect'
import {OverlayPanel} from 'primereact/overlaypanel'
import {Sidebar} from 'primereact/sidebar'
import {Tag} from 'primereact/tag'
import {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
} from 'react'
import {ReactComponent as Filter} from '../../assets/menu/Filter.svg'
import TranslateUtils from '../../i18n/TranslateUtils'
import {
    useCities,
    useCountries,
    useStates,
} from '../address/query-keys/AddressQueryManager'
import {useAuth} from '../authentication/AuthProvider'
import {component} from '../form/Constant'
import {isArrayAndNotEmpty} from '../observations/Utils'
import {
    filterEventTypes,
    filterExplainEvent,
    filterExplainEventTypes,
    getFilterExplainEventKey,
    onFilterChangeListener,
    onFilterExplainChangeListener,
    removeFilterModeChangeListener,
} from './broadcast/FilterEvent'
import {useDatatableContext} from './DataTableContext'
import {
    getAdvancedFilter,
    getAdvancedFilterByTableType,
    getShouldRememberAdvancedFilter,
    storeAdvancedFilter,
    storeShouldRememberAdvancedFilter,
} from './StyledTableUtils'
import './styles.css'

const StyledAdvancedFilter = forwardRef((props, ref) => {
    useSignals()

    useImperativeHandle(ref, () => ({
        showForm: handleShowForm,
        hideForm: handleHideForm,
    }))
    const {user} = useAuth()
    const {
        translateDatatable,
        translateKeys,
        translateCommons,
        translateOptions,
    } = TranslateUtils()

    const {tableType} = useDatatableContext()
    const overlayPanelRef = useRef(null)
    const visible = useSignal(false)
    const filterRequest = useSignal({})
    const shouldRememberFilterRequest = useSignal(false)
    const panelVisible = useSignal(false)

    const {data: countryOptions} = useCountries(user?.id)
    const {data: stateOptions} = useStates(
        user?.id,
        filterRequest.value?.refCountry,
    )
    const {data: cityOptions} = useCities(
        user?.id,
        filterRequest.value?.refCountry,
        filterRequest.value?.refState,
    )

    const filterDataList = useMemo(() => {
        if (!tableType) return []

        return getAdvancedFilterByTableType(tableType)
    }, [tableType])

    const handleShowForm = () => {
        visible.value = true

        if (Object.keys(filterRequest.value).length === 0) {
            const didRememberSettings =
                getShouldRememberAdvancedFilter(tableType)

            if (didRememberSettings) {
                const storedFilterRequest = getAdvancedFilter(tableType)
                if (
                    !!storedFilterRequest &&
                    Object.keys(storedFilterRequest).length > 0
                ) {
                    filterRequest.value = storedFilterRequest
                }
            } else {
                filterRequest.value = {}
            }

            shouldRememberFilterRequest.value = didRememberSettings
        }
    }

    const handleHideForm = () => {
        visible.value = false
    }

    const handleCheckShouldRemember = value => {
        shouldRememberFilterRequest.value = value

        storeShouldRememberAdvancedFilter(tableType, value)
    }

    //#region Render Content Elements
    const panelVisibilityHandler = () => ({
        optionLabel: 'label',
        optionValue: 'value',
        emptyFilterMessage: translateCommons('No results found'),
        resetFilterOnHide: true,
        filter: true,
        filterBy: 'label',
        filterPlaceholder: translateCommons('Search here'),
        showClear: true,
        onShow: () => (panelVisible.value = true),
        onHide: () => (panelVisible.value = false),
    })

    const panelVisiblilityProps = panelVisibilityHandler()

    const renderPanelFooterTemplate = selectedData => {
        let shouldRenderItemSelected = false
        let itemSelectedLength = 0

        if (!!selectedData && typeof selectedData === 'object') {
            if (isArrayAndNotEmpty(selectedData)) {
                shouldRenderItemSelected = true

                itemSelectedLength = selectedData?.length
            }

            if (
                !shouldRenderItemSelected &&
                Object.keys(selectedData)?.length > 0
            ) {
                shouldRenderItemSelected = true

                itemSelectedLength = Object.keys(selectedData)?.length
            }
        }

        return (
            <div className="p-3" style={{backgroundColor: '#f9f9f9'}}>
                <b>
                    {shouldRenderItemSelected && itemSelectedLength > 0
                        ? `${itemSelectedLength} ${
                              itemSelectedLength > 1 ? 'items' : 'item'
                          } selected`
                        : `No item selected`}
                </b>
            </div>
        )
    }

    const dropdownItemTemplate = (option, props) => {
        const selectedValue = filterRequest.value[props?.id]

        let selectedLabel = ''
        if (selectedValue !== null && typeof selectedValue === 'boolean') {
            const options = props?.options

            if (isArrayAndNotEmpty(options)) {
                const selectedOption = options.find(
                    item => item.value === selectedValue,
                )
                selectedLabel = selectedOption?.label
            }
        }

        if (option || selectedLabel) {
            return (
                <Tag
                    severity="success"
                    value={option?.label || selectedLabel}
                    style={{
                        backgroundColor: '#0f766e',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '1rem',
                        padding: '0.25rem 0.75rem',
                    }}></Tag>
            )
        }

        return <span>{props.placeholder}</span>
    }

    const filterGroupItem = useCallback(
        itemList => {
            if (!itemList) return []

            return itemList.map((item, index) =>
                item.component === component.multiSelect ? (
                    <div id={item.key} className="mb-1 col-12" key={item.key}>
                        <h6>{item.label}</h6>
                        <MultiSelect
                            id={item.key}
                            className="w-full h-3rem p-inputtext p-0 font-bold"
                            options={translateOptions(item.options)}
                            placeholder={translateCommons('Select an option')}
                            defaultValue={item.initialValue || null}
                            maxSelectedLabels={3}
                            display={
                                filterRequest.value[item.key]?.length < 4
                                    ? 'chip'
                                    : 'comma'
                            }
                            value={filterRequest.value[item.key] || null}
                            onChange={e => {
                                filterRequest.value = {
                                    ...filterRequest.value,
                                    [item.key]: e.value === null ? [] : e.value,
                                }
                            }}
                            panelFooterTemplate={() =>
                                renderPanelFooterTemplate(
                                    filterRequest.value[item.key],
                                )
                            }
                            {...panelVisiblilityProps}
                        />
                        {/* DESCRIPTION */}
                        {item?.description &&
                            (typeof item?.description === 'string' ? (
                                <p>{item?.description}</p>
                            ) : (
                                item?.description
                            ))}
                    </div>
                ) : item.component === component.dropdown ? (
                    <div
                        id={item.key}
                        className="mb-1 col-12 styled-dropdown"
                        key={item.key}>
                        <h6>{item.label}</h6>
                        <Dropdown
                            id={item.key}
                            className="w-full h-3rem font-bold"
                            options={translateOptions(item.options)}
                            placeholder={translateCommons('Select an option')}
                            value={filterRequest.value?.[item.key] || null}
                            onChange={e => {
                                if (e.value === null) {
                                    delete filterRequest.value[item.key]
                                    return
                                }

                                filterRequest.value = {
                                    ...filterRequest.value,
                                    [item.key]: e.value || null,
                                }
                            }}
                            valueTemplate={dropdownItemTemplate}
                            {...panelVisiblilityProps}
                        />
                    </div>
                ) : item.component === component.address ? (
                    <div
                        id={item.key}
                        key={item.key}
                        className=" mb-3 styled-dropdown">
                        <h6>Country</h6>
                        <Dropdown
                            id={item.key}
                            className="w-full h-3rem font-bold"
                            options={translateOptions(countryOptions)}
                            placeholder={translateCommons('Select an option')}
                            value={filterRequest.value?.refCountry || null}
                            onChange={e => {
                                filterRequest.value = {
                                    ...filterRequest.value,
                                    refCountry: e.value,
                                }
                            }}
                            valueTemplate={dropdownItemTemplate}
                            {...panelVisiblilityProps}
                        />
                        <h6>State</h6>
                        <Dropdown
                            id={item.key}
                            className="w-full h-3rem font-bold"
                            options={translateOptions(stateOptions)}
                            placeholder={translateCommons('Select an option')}
                            value={filterRequest.value?.refState || null}
                            onChange={e => {
                                filterRequest.value = {
                                    ...filterRequest.value,
                                    refState: e.value,
                                }
                            }}
                            valueTemplate={dropdownItemTemplate}
                            {...panelVisiblilityProps}
                        />
                        <h6>City</h6>
                        <Dropdown
                            id={item.key}
                            className="w-full h-3rem font-bold"
                            options={translateOptions(cityOptions)}
                            placeholder={translateCommons('Select an option')}
                            value={filterRequest.value?.city || null}
                            onChange={e => {
                                filterRequest.value = {
                                    ...filterRequest.value,
                                    city: e.value,
                                }
                            }}
                            valueTemplate={dropdownItemTemplate}
                            {...panelVisiblilityProps}
                        />
                    </div>
                ) : item.component === component.checkbox ? (
                    <div id={item.key} key={item.key} className="text-lg mb-1">
                        <div className="flex align-items-center mb-3">
                            <div className="mr-2">
                                <Checkbox
                                    checked={true}
                                    onChange={e => {
                                        filterRequest.value = {
                                            ...filterRequest.value,
                                            [item.key]: e.checked,
                                        }
                                    }}
                                />
                            </div>
                            <div>
                                <span>{item.label}</span>
                            </div>
                        </div>
                    </div>
                ) : null,
            )
        },
        [countryOptions, stateOptions, cityOptions],
    )

    const filterGroup = useMemo(() => {
        if (!filterDataList) return []

        return filterDataList?.map((item, index) => (
            <div
                id={item.key}
                key={item.key}
                className="grid col-12 m-0 p-0 mb-4"
                style={{
                    borderBottom: '1px dashed lightgray',
                    marginBottom: '15px',
                }}>
                <div className="col-12 m-0 p-0" style={{position: 'relative'}}>
                    <div className="sticky">
                        <div className="mb-2">
                            <h6 className="mb-1 font-bold underline-60">
                                {item.label}
                            </h6>
                        </div>
                    </div>
                </div>
                {item?.children?.length > 0 && (
                    <div className="col-12 p-0 mb-3">
                        {filterGroupItem(item.children)}
                    </div>
                )}
            </div>
        ))
    }, [
        filterDataList,
        countryOptions,
        stateOptions,
        cityOptions,
        filterRequest.value,
    ])

    const menuList = useMemo(() => {
        if (!filterDataList) return []
        return filterDataList?.map((item, index) => (
            <div
                key={item.key}
                className="mt-2 p-3 border-round-lg"
                style={{background: '#f9f9f9'}}>
                <h6 className="font-bold m-0 p-1">
                    <a
                        className=" text-700"
                        style={{color: 'rgb(73, 80, 87'}}
                        href={`#${item.key}`}>
                        <i className="pi pi-sitemap" />{' '}
                        {translateKeys(item.label)}
                    </a>
                </h6>
                <ul className="ul list-none ">
                    {item?.children?.map(itemMenu => (
                        <li key={itemMenu.key} className="p-2">
                            <a
                                className="flex gap-2 hover-text"
                                href={`#${itemMenu.key}`}>
                                <i
                                    className="pi pi-chevron-circle-right"
                                    style={{fontSize: '0.9rem'}}
                                />{' '}
                                {translateKeys(itemMenu.label)}
                            </a>
                        </li>
                    ))}
                </ul>
            </div>
        ))
    }, [filterDataList])
    //#endregion

    //#region Handle Events
    const handleRestoreFilterRequest = () => {
        filterRequest.value = {}
        shouldRememberFilterRequest.value = false

        storeAdvancedFilter(tableType, {})
        storeShouldRememberAdvancedFilter(tableType, false)

        onFilterChangeListener(tableType, filterEventTypes.advanced, {
            ...filterRequest.value,
        })

        onFilterExplainChangeListener(
            tableType,
            filterExplainEventTypes.ADVANCED_FILTER_EXPLAIN_CHANGE,
            [],
        )
    }

    const handleSubmitFilterRequest = () => {
        storeAdvancedFilter(tableType, filterRequest.value)
        storeShouldRememberAdvancedFilter(
            tableType,
            shouldRememberFilterRequest.value,
        )

        onFilterChangeListener(tableType, filterEventTypes.advanced, {
            ...filterRequest.value,
        })

        onFilterExplainChangeListener(
            tableType,
            filterExplainEventTypes.ADVANCED_FILTER_EXPLAIN_CHANGE,
            Object.entries(filterRequest.value).map(([key, value]) => ({
                [key]: value,
            })),
        )

        handleHideForm()
    }

    useEffect(() => {
        filterExplainEvent.on(
            `${getFilterExplainEventKey(
                tableType,
                filterExplainEventTypes.CLEAR_ALL_FILTER,
            )}`,
            eventType => {
                if (eventType === filterExplainEventTypes.CLEAR_ALL_FILTER) {
                    filterRequest.value = {}

                    storeAdvancedFilter(tableType, {})
                }
            },
        )

        filterExplainEvent.on(
            `${getFilterExplainEventKey(
                tableType,
                filterExplainEventTypes.CLEAR_ADVANCED_FILTER,
            )}`,
            () => {
                filterRequest.value = {}

                storeAdvancedFilter(tableType, {})
            },
        )

        return () => {
            removeFilterModeChangeListener(
                tableType,
                filterExplainEventTypes.CLEAR_ALL_FILTER,
            )

            removeFilterModeChangeListener(
                tableType,
                filterExplainEventTypes.CLEAR_ADVANCED_FILTER,
            )
        }
    }, [])
    //#endregion

    //#region Prevent Scroll Helper
    useSignalEffect(() => {
        const contentDiv = document.querySelector(
            '#sidebar-advanced-filter .p-sidebar-content',
        )

        toggleBlockScroll(contentDiv, panelVisible.value)

        return () => {
            if (!!contentDiv) {
                contentDiv.removeEventListener('wheel', preventScroll)
                contentDiv.removeEventListener('touchmove', preventScroll)
            }
        }
    })

    const preventScroll = event => {
        event.preventDefault()
        event.stopPropagation()
    }

    const toggleBlockScroll = useCallback((contentDiv, isVisible) => {
        if (!!contentDiv) {
            if (!!isVisible) {
                contentDiv.addEventListener('wheel', preventScroll, {
                    passive: false,
                })
                contentDiv.addEventListener('touchmove', preventScroll, {
                    passive: false,
                })
            } else {
                contentDiv.removeEventListener('wheel', preventScroll)
                contentDiv.removeEventListener('touchmove', preventScroll)
            }
        }
    }, [])
    //#endregion

    return (
        <Sidebar
            id="sidebar-advanced-filter"
            className="sidebar-filter"
            visible={visible.value}
            onHide={handleHideForm}
            showCloseIcon={true}
            blockScroll={true}
            position="right">
            <div className="py-2 px-1">
                {/* Header */}
                <div className="flex align-items-center">
                    <div className="mr-2">
                        <Filter style={{fontSize: '30px'}} />
                    </div>
                    <div>
                        <h3 className="font-bold m-0">Advanced Filter</h3>
                    </div>
                </div>
                <div>
                    <p>
                        An advanced filter allows users to apply more complex
                        filtering criteria to a table of data
                    </p>
                </div>
                <hr />
                {/* Content */}
                <div
                    style={{
                        height: 'calc(100vh - 25vh)',
                    }}>
                    {!!filterDataList && filterDataList.length > 0 ? (
                        <div
                            id="filterWrapper"
                            className="grid w-full h-full "
                            style={{overflowY: 'auto'}}>
                            <div className="col-7">
                                <div className="flex align-items-center mb-3">
                                    <div>
                                        <i className="pi pi-sliders-h mr-2 icon-size"></i>
                                    </div>
                                    <div>
                                        <h5 className="mb-0 font-bold">
                                            {translateDatatable(
                                                'Filter Fields',
                                            )}{' '}
                                        </h5>
                                    </div>
                                </div>
                                {filterGroup}
                            </div>
                            <div
                                className="lg:col-5 "
                                style={{position: 'relative'}}>
                                <div className="sticky">
                                    <div className="flex align-items-center mb-3">
                                        <div>
                                            <i className="pi pi-list mr-2 icon-size"></i>
                                        </div>
                                        <div>
                                            <h5 className="mb-0 font-bold">
                                                {translateDatatable(
                                                    'Filter Menu List',
                                                )}
                                            </h5>
                                        </div>
                                    </div>

                                    <div>{menuList}</div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="col-12">
                            <h6 className="mb-0">
                                {translateDatatable('No Filter Available')}
                            </h6>
                        </div>
                    )}
                </div>
                {/* Footer */}
                <div className="my-2">
                    <div className="flex align-items-center my-3">
                        <div className="mr-2">
                            <Checkbox
                                checked={shouldRememberFilterRequest.value}
                                onChange={e => {
                                    handleCheckShouldRemember(e.checked)
                                }}
                            />
                        </div>
                        <div>
                            <span>
                                Remember my advanced filter settings{' '}
                                <span
                                    className="cursor-pointer"
                                    onMouseEnter={e =>
                                        overlayPanelRef.current.show(e)
                                    }
                                    onMouseLeave={e =>
                                        overlayPanelRef.current.hide(e)
                                    }>
                                    <i className="pi pi-question-circle"></i>
                                </span>
                            </span>
                        </div>
                    </div>
                    <Button
                        label={translateDatatable(
                            'RESTORE TO DEFAULT',
                        ).toUpperCase()}
                        icon="pi pi-refresh"
                        className="col-12 p-button-lg p-button-outlined font-bold mb-2"
                        onClick={() => {
                            handleRestoreFilterRequest()
                        }}
                    />
                    <Button
                        label={translateDatatable('Submit').toUpperCase()}
                        icon="pi pi-send"
                        className="col-12 p-button-lg font-bold"
                        onClick={() => {
                            handleSubmitFilterRequest()
                        }}
                    />
                </div>

                <div id="advanced-filter-explaination">
                    <OverlayPanel
                        ref={overlayPanelRef}
                        style={{
                            zIndex: 9999,
                        }}
                        showCloseIcon={false}>
                        <div>
                            <i className="pi pi-info-circle mr-2"></i>
                            <span className="font-bold">Explaination</span>
                        </div>
                        <hr />
                        <p
                            style={{
                                maxWidth: '220px',
                                marginBottom: '10px',
                            }}>
                            This feature allows you to save the filters you
                            apply during your session.{' '}
                            <span className="text-darkcyan">When enabled</span>,
                            your filter choices are{' '}
                            <span className="text-darkcyan">
                                automatically saved
                            </span>{' '}
                            and will{' '}
                            <span className="text-darkcyan">be reapplied</span>{' '}
                            the next time you visit the same page. This helps
                            you quickly access the specific data you're
                            interested in{' '}
                            <span className="text-darkcyan">
                                without needing to set up your filters again
                            </span>
                            . Whether you're frequently refining search results
                            or viewing specific data sets, this option ensures
                            that your personalized settings are consistently
                            maintained, saving you time and effort.
                        </p>
                    </OverlayPanel>
                </div>
            </div>
        </Sidebar>
    )
})

export default StyledAdvancedFilter
